import { ApiService } from '../../services/api';
import { CropEmbeddingSearchResponse, DesignResponse } from '../../types';
import {
  AddVizSimDatasetCropsRequest,
  AddVizSimDatasetCropsResponse,
  ApiMasterLabelBarcodeLookupResponse,
  ApiMasterLabelCropLookupResponse,
  ApiMasterLabelDesignLookupResponse,
  ApiMasterLabelLinkTargetsRequest,
  ApiMasterLabelLinkTargetsResponse,
  ApiMasterLabelUPCData,
  BarcodeStatus,
  CleaningChainHistoryResponse,
  CleaningChainStatsResponse,
  CleaningDashboardResponse,
  CleaningFamily,
  CleaningFinding,
  CleaningIssueScope,
  CleaningIssueType,
  CleaningItemScore,
  CleaningItemsResponse,
  CleaningLockFilter,
  CleaningLockStatus,
  CleaningRefreshRun,
  CleaningSeverity,
  CleaningSort,
  CreateDesignLockRequest,
  CropLifecycleResponse,
  DataCollectionCompleteBatchResponse,
  DataCollectionCreateRequest,
  DataCollectionCreateResponse,
  DataCollectionDashboardResponse,
  DataCollectionRequestedType,
  DataCollectionRequestRow,
  DataCollectionReviewBatchResponse,
  DataCollectionStatus,
  DataCollectionTargetScope,
  DesignLockDetailResponse,
  DesignLockListEntry,
  DesignLockListResponse,
  DesignLockMutationResponse,
  DesignLockStatus,
  DesignLockSummaryResponse,
  EnsureVizSimDatasetRequest,
  FetchDesignLocksRequest,
  ForgeBarcodeReviewStatusUpdateRequest,
  ForgeBarcodeReviewStatusUpdateResponse,
  ForgeDesignGroupNameUpdateResponse,
  ForgeDesignGroupsDashboardResponse,
  ForgeDesignGroupSearchResponse,
  ForgeDesignGroupsRefreshRequest,
  ForgeDesignGroupsScopeChangeRequest,
  ForgeDesignsDashboardResponse,
  ForgeDesignsRefreshRequest,
  ForgeReviewIssueActiveFilter,
  ForgeReviewIssueAssignmentFilter,
  ForgeReviewIssuesChainSnapshot,
  ForgeReviewIssuesDashboardResponse,
  ForgeReviewIssueSort,
  ForgeReviewSort,
  ForgeReviewStatusUpdateRequest,
  ForgeReviewStatusUpdateResponse,
  ForgeSupportedChainsResponse,
  ForgeSystemHealthDashboardResponse,
  ForgeSystemHealthRefreshRequest,
  ForgeSystemHealthRefreshRunResponse,
  ItemModPrefixEntry,
  ItemModPrefixesResponse,
  MasterDataCollectionDetailResponse,
  MasterDataCollectionImagePreviewResponse,
  MasterDataCollectionMasterLabelStateSaveRequest,
  MasterDataCollectionMasterLabelStateSaveResponse,
  MasterLabelCropQueryResponse,
  MasterLabelReviewIssueType,
  MasterLabelStateSaveRequest,
  MasterLabelStateSaveResponse,
  MasterLabelUnresolvedChainSnapshot,
  MasterLabelUnresolvedDashboardResponse,
  MasterLabelUnresolvedFilter,
  MasterLabelUnresolvedWorkItemsResponse,
  ModifyForgeDesignGroupsRequest,
  ModifyForgeDesignGroupsResponse,
  ProductRecognitionInferenceAggregateResponse,
  ProductRecognitionInferenceDashboardResponse,
  ProductRecognitionInferencePlotAnnotation,
  ProductRecognitionInferencePlotAnnotationRequest,
  ProductRecognitionInferencePlotAnnotationsResponse,
  ProductRecognitionInferenceRefreshRequest,
  ProductRecognitionInferenceRefreshRunResponse,
  RecognitionBulkConfirmRequest,
  RecognitionBulkConfirmResponse,
  RecognitionConfidentCorrectBucketCleanupResponse,
  RecognitionOkrAggregatesParams,
  RecognitionOkrAggregatesResponse,
  RecognitionReviewAccessResponse,
  RecognitionReviewBarcodeLookupResponse,
  RecognitionReviewQueueParams,
  RecognitionReviewQueueResponse,
  RecognitionReviewRequest,
  RecognitionReviewSaveResponse,
  RecognitionReviewSliceParams,
  RecognitionReviewSliceResponse,
  RecognitionReviewUnitDetailResponse,
  RecognitionReviewUnitParams,
  RecognitionSamplingStrategyRequest,
  RecognitionSamplingStrategyResponse,
  RecognitionThresholdConfiguration,
  RecognitionThresholdDraftRequest,
  ReleasedVizSimCropIdsResponse,
  ReleaseVizSimDatasetRequest,
  RemoteDataCollectionCategoryOptionsResponse,
  RemoteDataCollectionChainPresets,
  RemoteDataCollectionChainSnapshot,
  RemoteDataCollectionConfig,
  RemoteDataCollectionConfigUpdateRequest,
  RemoteDataCollectionDailyPreviewsResponse,
  RemoteDataCollectionDashboardResponse,
  RemoteDataCollectionDefaultPresetUpdateRequest,
  RemoteDataCollectionDefaults,
  RemoteDataCollectionImageTimelineResponse,
  RemoteDataCollectionPresetCreateRequest,
  RemoteDataCollectionPresetDeleteRequest,
  RemoteDataCollectionPresetsResponse,
  RemoteDataCollectionPresetUpdateRequest,
  RemoteDataCollectionRefreshRequest,
  RemoteDataCollectionReviewTargetsResponse,
  RemoteDataCollectionRunResponse,
  RemoveVizSimDatasetCropsResponse,
  ReportedIssuesFilters,
  ReportedIssuesResponse,
  UpdateVizSimDatasetCropRequest,
  VizSimDatasetCrop,
  VizSimDatasetDetailResponse,
  VizSimDatasetListResponse,
  VizSimDatasetTagSuggestionsRequest,
  VizSimDatasetTagSuggestionsResponse,
} from '../types';
import {
  ForgeApiError,
  forgeBlobRequest,
  forgeFetch,
  forgeRequest,
  forgeVoidRequest,
  parseApiError,
} from './forgeRequest';

export { ForgeApiError } from './forgeRequest';

function normalizeApiDlUrl(url: string): string {
  try {
    const parsedUrl = new URL(url);
    if (parsedUrl.protocol === 'http:' && ['localhost', '127.0.0.1'].includes(parsedUrl.hostname)) {
      if (process.env.NODE_ENV === 'development') {
        // The local app proxies API DL through the same origin. This keeps the browser on the
        // slot's frontend port and avoids direct cross-origin access to the slot API.
        return '/api-dl';
      }
      parsedUrl.hostname = '127.0.0.1';
      return parsedUrl.toString().replace(/\/$/, '');
    }
  } catch {
    return url;
  }

  return url.replace(/\/$/, '');
}

const API_DL_URL = process.env.REACT_APP_API_DL_URL
  ? normalizeApiDlUrl(process.env.REACT_APP_API_DL_URL)
  : undefined;

if (!API_DL_URL) {
  throw new Error('REACT_APP_API_DL_URL is required for Forge API requests');
}

const FORGE_ADMIN_API_URL = `${API_DL_URL}/v1/forge/admin`;
const FORGE_CROPS_API_URL = `${API_DL_URL}/v1/forge/crops`;
const FORGE_DESIGNS_API_URL = `${API_DL_URL}/v1/forge/designs`;
const FORGE_DESIGN_LOCKS_API_URL = `${API_DL_URL}/v1/forge/design-locks`;
const CONTROL_EMBEDDING_API_URL = `${API_DL_URL}/v1/control/embedding`;
const FORGE_DASHBOARDS_API_URL = `${FORGE_ADMIN_API_URL}/dashboards`;
const FORGE_DATA_COLLECTION_API_URL = `${FORGE_ADMIN_API_URL}/data-collection`;
const FORGE_DESIGN_GROUPS_API_URL = `${FORGE_ADMIN_API_URL}/design-groups`;
const FORGE_MASTER_DATA_COLLECTION_API_URL = `${FORGE_ADMIN_API_URL}/master-data-collection`;
const FORGE_MASTER_LABEL_UNRESOLVED_API_URL = `${FORGE_ADMIN_API_URL}/unresolved-master-label`;
const FORGE_MASTER_LABEL_API_URL = `${API_DL_URL}/v1/forge/labeling-tools/master-label`;
const FORGE_PR_REVIEW_API_URL = `${FORGE_ADMIN_API_URL}/pr-review`;
const FORGE_REMOTE_DATA_COLLECTION_API_URL = `${FORGE_ADMIN_API_URL}/remote-data-collection`;
const FORGE_VIZSIM_DATASETS_API_URL = `${FORGE_ADMIN_API_URL}/vizsim-datasets`;
const FORGE_CLEANING_API_URL = `${FORGE_ADMIN_API_URL}/cleaning-tasks`;

type CleaningItemsQueryParams = {
  chainId: number;
  page?: number;
  pageSize?: number;
  severity?: CleaningSeverity | 'all';
  family?: CleaningFamily;
  sort?: CleaningSort;
  search?: string;
  excluded?: 'hide' | 'only';
  duplicates?: 'hide' | 'show';
  issueTypes?: CleaningIssueType[];
  issueScope?: CleaningIssueScope | 'all';
  locks?: CleaningLockFilter;
  modPrefixes?: string[];
  /** Focus filter: items assessed on, or flagged for, these subscores. */
  focusSubscores?: string[];
  limit?: number | null;
};

function buildCleaningItemsQuery(params: CleaningItemsQueryParams): URLSearchParams {
  const query = new URLSearchParams({ chain_id: String(params.chainId) });
  if (params.page !== undefined) query.set('page', String(params.page));
  if (params.pageSize !== undefined) query.set('page_size', String(params.pageSize));
  if (params.severity !== undefined) query.set('severity', params.severity);
  if (params.family !== undefined) query.set('family', params.family);
  if (params.sort !== undefined) query.set('sort', params.sort);
  if (params.excluded !== undefined) query.set('excluded', params.excluded);
  if (params.duplicates !== undefined) query.set('duplicates', params.duplicates);
  params.issueTypes?.forEach((issueType) => query.append('issue_type', issueType));
  if (params.issueScope !== undefined) query.set('issue_scope', params.issueScope);
  if (params.locks !== undefined) query.set('locks', params.locks);
  params.modPrefixes?.forEach((prefix) => query.append('mod_prefix', prefix));
  params.focusSubscores?.forEach((subscore) => query.append('focus_subscore', subscore));
  if (params.limit !== undefined && params.limit !== null) query.set('limit', String(params.limit));
  const search = params.search?.trim();
  if (search) query.set('search', search);
  return query;
}
const REPORTED_ISSUES_API_URL = `${API_DL_URL}/v1/image/reported-issues`;

type CleaningSubscoresWire = CleaningItemScore['subscores'] & {
  class_coherence?: number | null;
  class_confusion?: number | null;
  front_split?: number | null;
  front_strays?: number | null;
  front_fragmentation?: number | null;
  front_confusion?: number | null;
  duplicate_front_classes?: number | null;
  front_identity?: number | null;
  identity_resolution?: number | null;
  review_flags?: number | null;
  metadata_integrity?: number | null;
  barcode_mix?: number | null;
  carton_confusion?: number | null;
  nonfront_confusion?: number | null;
  partial_group_front_alignment?: number | null;
  nonfront_group_alignment?: number | null;
  review_load?: number | null;
};

type CleaningFindingWire = NonNullable<CleaningItemScore['finding']> & {
  design_id?: string | null;
  issue_type?: CleaningFinding['issueType'];
  issue_scope?: CleaningFinding['issueScope'];
};

type CleaningItemCountsWire = CleaningItemScore['counts'] & {
  mixed_barcodes?: number;
  review_flagged?: number;
  metadata_issues?: number;
};

type CleaningItemScoreWire = Omit<
  CleaningItemScore,
  'subscores' | 'finding' | 'findings' | 'counts' | 'taskCoveredBy'
> & {
  exclusion_outdated?: boolean;
  previous_score?: number | null;
  previous_severity?: string | null;
  mod_prefix?: string | null;
  locked_design_count?: number | null;
  lock_status?: CleaningLockStatus | null;
  taskCoveredBy?: CleaningItemScore['taskCoveredBy'];
  task_covered_by?: CleaningItemScore['taskCoveredBy'];
  subscores: CleaningSubscoresWire;
  finding?: CleaningFindingWire | null;
  findings?: CleaningFindingWire[];
  counts: CleaningItemCountsWire;
};

function normalizeCleaningFinding(finding: CleaningFindingWire): CleaningFinding {
  const { design_id, issue_type, issue_scope, ...findingRest } = finding;
  return {
    ...findingRest,
    designId: findingRest.designId ?? design_id,
    issueType: findingRest.issueType ?? issue_type,
    issueScope: findingRest.issueScope ?? issue_scope,
  };
}

function normalizeCleaningItem(item: CleaningItemScoreWire): CleaningItemScore {
  const {
    exclusion_outdated,
    previous_score,
    previous_severity,
    mod_prefix,
    locked_design_count,
    lock_status,
    task_covered_by,
    findings,
    ...itemRest
  } = item;
  const {
    class_coherence,
    class_confusion,
    front_split,
    front_strays,
    front_fragmentation,
    front_confusion,
    duplicate_front_classes,
    front_identity,
    identity_resolution,
    review_flags,
    metadata_integrity,
    barcode_mix,
    carton_confusion,
    nonfront_confusion,
    partial_group_front_alignment,
    nonfront_group_alignment,
    review_load,
    ...subscores
  } = item.subscores;
  const { mixed_barcodes, review_flagged, metadata_issues, ...counts } = item.counts;
  const finding = item.finding ? normalizeCleaningFinding(item.finding) : item.finding;

  return {
    ...itemRest,
    exclusionOutdated: itemRest.exclusionOutdated ?? exclusion_outdated,
    previousScore: itemRest.previousScore !== undefined ? itemRest.previousScore : previous_score,
    previousSeverity:
      itemRest.previousSeverity !== undefined ? itemRest.previousSeverity : previous_severity,
    modPrefix: itemRest.modPrefix !== undefined ? itemRest.modPrefix : mod_prefix,
    lockedDesignCount:
      itemRest.lockedDesignCount !== undefined ? itemRest.lockedDesignCount : locked_design_count,
    lockStatus: itemRest.lockStatus !== undefined ? itemRest.lockStatus : lock_status,
    taskCoveredBy: itemRest.taskCoveredBy ?? task_covered_by,
    subscores: {
      ...subscores,
      classCoherence: subscores.classCoherence ?? class_coherence,
      classConfusion: subscores.classConfusion ?? class_confusion,
      frontSplit: subscores.frontSplit ?? front_split,
      frontStrays: subscores.frontStrays ?? front_strays,
      frontFragmentation: subscores.frontFragmentation ?? front_fragmentation,
      frontConfusion: subscores.frontConfusion ?? front_confusion,
      duplicateFrontClasses: subscores.duplicateFrontClasses ?? duplicate_front_classes,
      frontIdentity: subscores.frontIdentity ?? front_identity,
      identityResolution: subscores.identityResolution ?? identity_resolution,
      reviewFlags: subscores.reviewFlags ?? review_flags,
      metadataIntegrity: subscores.metadataIntegrity ?? metadata_integrity,
      barcodeMix: subscores.barcodeMix ?? barcode_mix,
      cartonConfusion: subscores.cartonConfusion ?? carton_confusion,
      nonfrontConfusion: subscores.nonfrontConfusion ?? nonfront_confusion,
      partialGroupFrontAlignment:
        subscores.partialGroupFrontAlignment ?? partial_group_front_alignment,
      nonfrontGroupAlignment: subscores.nonfrontGroupAlignment ?? nonfront_group_alignment,
      reviewLoad: subscores.reviewLoad ?? review_load,
    },
    finding,
    findings: findings?.map(normalizeCleaningFinding),
    counts: {
      ...counts,
      mixedBarcodes: counts.mixedBarcodes ?? mixed_barcodes,
      reviewFlagged: counts.reviewFlagged ?? review_flagged,
      metadataIssues: counts.metadataIssues ?? metadata_issues,
    },
  };
}

type CleaningItemsWireResponse = CleaningItemsResponse & {
  issue_type_counts?: CleaningItemsResponse['issueTypeCounts'];
  issue_scope_counts?: CleaningItemsResponse['issueScopeCounts'];
  mod_prefix_counts?: CleaningItemsResponse['modPrefixCounts'];
};

function normalizeCleaningItemsResponse(payload: CleaningItemsWireResponse): CleaningItemsResponse {
  return {
    ...payload,
    items: payload.items.map((item) => normalizeCleaningItem(item as CleaningItemScoreWire)),
    issueTypeCounts: payload.issueTypeCounts ?? payload.issue_type_counts,
    issueScopeCounts: payload.issueScopeCounts ?? payload.issue_scope_counts,
    modPrefixCounts: payload.modPrefixCounts ?? payload.mod_prefix_counts,
  };
}

type ApiReviewStatusUpdateResponse = {
  group_id?: string;
  groupId?: string;
  is_reviewed?: boolean;
  isReviewed?: boolean;
  reviewed_by?: string | null;
  reviewedBy?: string | null;
  reviewed_at?: string | null;
  reviewedAt?: string | null;
};

function normalizeReviewStatusUpdateResponse(
  payload: ApiReviewStatusUpdateResponse,
  groupId: string,
  fallbackIsReviewed: boolean
): ForgeReviewStatusUpdateResponse {
  return {
    groupId: payload.groupId ?? payload.group_id ?? groupId,
    isReviewed: payload.isReviewed ?? payload.is_reviewed ?? fallbackIsReviewed,
    reviewedBy: payload.reviewedBy ?? payload.reviewed_by ?? null,
    reviewedAt: payload.reviewedAt ?? payload.reviewed_at ?? null,
  };
}

const inFlightProductRecognitionInferenceRequests = new Map<
  string,
  Promise<ProductRecognitionInferenceDashboardResponse>
>();
const productRecognitionInferenceDashboardCache = new Map<
  string,
  ProductRecognitionInferenceDashboardResponse
>();
const inFlightProductRecognitionInferenceAggregateRequests = new Map<
  string,
  Promise<ProductRecognitionInferenceAggregateResponse>
>();
const productRecognitionInferenceAggregateCache = new Map<
  string,
  ProductRecognitionInferenceAggregateResponse
>();
const inFlightProductRecognitionInferenceAnnotationRequests = new Map<
  string,
  Promise<ProductRecognitionInferencePlotAnnotationsResponse>
>();
let productRecognitionInferenceCacheGeneration = 0;

function clearProductRecognitionInferenceCaches(): void {
  productRecognitionInferenceCacheGeneration += 1;
  productRecognitionInferenceDashboardCache.clear();
  productRecognitionInferenceAggregateCache.clear();
  inFlightProductRecognitionInferenceRequests.clear();
  inFlightProductRecognitionInferenceAggregateRequests.clear();
  inFlightProductRecognitionInferenceAnnotationRequests.clear();
}

// api-dl HttpModel responses serialize with camelCase aliases (e.g. `pageSize`, `designId`),
// so the wire shapes below are camelCase even though request bodies/query params stay snake_case.
type ApiDesignLockStatusInfo = {
  designId: string;
  status: DesignLockStatus;
  cropsChanged: boolean;
  metadataChanged: boolean;
  groupsChanged: boolean;
  lockedActiveCropCount: number;
  liveActiveCropCount: number;
  lockedBy: string;
  lockedAt: string;
  updatedBy?: string | null;
  updatedAt?: string | null;
  contentHashVersion: number;
};

type ApiDesignLockListEntry = ApiDesignLockStatusInfo & {
  normalizedBarcode?: string | null;
  representativeCropId?: string | null;
  representativeVizUrl?: string | null;
  cropCount: number;
};

type ApiDesignLockListResponse = {
  locks: ApiDesignLockListEntry[];
  total: number;
  page: number;
  pageSize: number;
};

function mapDesignLockStatusInfo(lock: ApiDesignLockStatusInfo): DesignLockMutationResponse {
  return {
    designId: lock.designId,
    status: lock.status,
    cropsChanged: lock.cropsChanged,
    metadataChanged: lock.metadataChanged,
    groupsChanged: lock.groupsChanged,
    lockedActiveCropCount: lock.lockedActiveCropCount,
    liveActiveCropCount: lock.liveActiveCropCount,
    lockedBy: lock.lockedBy,
    lockedAt: lock.lockedAt,
    updatedBy: lock.updatedBy,
    updatedAt: lock.updatedAt,
    contentHashVersion: lock.contentHashVersion,
  };
}

function mapDesignLockListEntry(lock: ApiDesignLockListEntry): DesignLockListEntry {
  return {
    ...mapDesignLockStatusInfo(lock),
    normalizedBarcode: lock.normalizedBarcode ?? '',
    representativeCropId: lock.representativeCropId ?? '',
    representativeVizUrl: lock.representativeVizUrl,
    cropCount: lock.cropCount,
  };
}

export async function fetchSupportedChains(token: string): Promise<ForgeSupportedChainsResponse> {
  return forgeRequest<ForgeSupportedChainsResponse>(
    token,
    `${FORGE_ADMIN_API_URL}/supported-chains`,
    'Error fetching supported Forge chains'
  );
}

export async function fetchDataCollectionDashboard(
  token: string,
  filters?: {
    status?: DataCollectionStatus | 'all';
    chainId?: number | null;
    targetScope?: DataCollectionTargetScope | 'all' | null;
    requestedType?: DataCollectionRequestedType | 'all' | null;
    requestedBy?: string | 'all' | null;
    groupBy?: 'item_number' | null;
    storeId?: number | null;
  }
): Promise<DataCollectionDashboardResponse> {
  const params = new URLSearchParams();
  if (filters?.status) params.set('status', filters.status);
  if (filters?.chainId) params.set('chain_id', String(filters.chainId));
  if (filters?.storeId) params.set('store_id', String(filters.storeId));
  if (filters?.targetScope && filters.targetScope !== 'all') {
    params.set('target_scope', filters.targetScope);
  }
  if (filters?.requestedType && filters.requestedType !== 'all') {
    params.set('requested_type', filters.requestedType);
  }
  if (filters?.requestedBy && filters.requestedBy !== 'all') {
    params.set('requested_by', filters.requestedBy);
  }
  if (filters?.groupBy) params.set('group_by', filters.groupBy);
  const query = params.toString();
  return forgeRequest<DataCollectionDashboardResponse>(
    token,
    `${FORGE_DASHBOARDS_API_URL}/data-collection${query ? `?${query}` : ''}`,
    'Error fetching data collection dashboard'
  );
}

export async function fetchReportedIssues(
  token: string,
  filters?: ReportedIssuesFilters
): Promise<ReportedIssuesResponse> {
  const params = new URLSearchParams();
  if (filters?.reason) params.set('reason', filters.reason);
  if (filters?.source) params.set('source', filters.source);
  if (filters?.imageId) params.set('image_id', filters.imageId);
  if (filters?.chainId != null) params.set('chain_id', String(filters.chainId));
  if (filters?.storeId != null) params.set('store_id', String(filters.storeId));
  if (filters?.skip != null) params.set('skip', String(filters.skip));
  if (filters?.limit != null) params.set('limit', String(filters.limit));
  const query = params.toString();
  return forgeRequest<ReportedIssuesResponse>(
    token,
    `${REPORTED_ISSUES_API_URL}${query ? `?${query}` : ''}`,
    'Error fetching reported issues'
  );
}

export async function createDataCollectionRequest(
  token: string,
  request: DataCollectionCreateRequest
): Promise<DataCollectionCreateResponse> {
  return forgeRequest<DataCollectionCreateResponse>(
    token,
    `${FORGE_DATA_COLLECTION_API_URL}/requests`,
    'Error creating data collection request',
    { method: 'POST', body: request }
  );
}

export async function completeDataCollectionRequest(
  token: string,
  requestId: string
): Promise<DataCollectionRequestRow> {
  return forgeRequest<DataCollectionRequestRow>(
    token,
    `${FORGE_DATA_COLLECTION_API_URL}/requests/${encodeURIComponent(requestId)}/complete`,
    'Error completing data collection request',
    { method: 'POST' }
  );
}

export async function completeDataCollectionRequestsBatch(
  token: string,
  requestIds: string[]
): Promise<DataCollectionCompleteBatchResponse> {
  return forgeRequest<DataCollectionCompleteBatchResponse>(
    token,
    `${FORGE_DATA_COLLECTION_API_URL}/requests/complete-batch`,
    'Error completing data collection requests',
    { method: 'POST', body: { request_ids: requestIds } }
  );
}

export async function reviewDataCollectionRequest(
  token: string,
  requestId: string
): Promise<DataCollectionRequestRow> {
  return forgeRequest<DataCollectionRequestRow>(
    token,
    `${FORGE_DATA_COLLECTION_API_URL}/requests/${encodeURIComponent(requestId)}/review`,
    'Error reviewing data collection request',
    { method: 'POST' }
  );
}

export async function reviewDataCollectionRequestsBatch(
  token: string,
  requestIds: string[]
): Promise<DataCollectionReviewBatchResponse> {
  return forgeRequest<DataCollectionReviewBatchResponse>(
    token,
    `${FORGE_DATA_COLLECTION_API_URL}/requests/review-batch`,
    'Error reviewing data collection requests',
    { method: 'POST', body: { request_ids: requestIds } }
  );
}

export async function fetchMasterLabelUnresolved(
  token: string
): Promise<MasterLabelUnresolvedDashboardResponse> {
  return forgeRequest<MasterLabelUnresolvedDashboardResponse>(
    token,
    `${FORGE_DASHBOARDS_API_URL}/master-label-unresolved`,
    'Error fetching Master Label unresolved list',
    { dedupeKey: ['fetchMasterLabelUnresolved', token] }
  );
}

export async function refreshMasterLabelUnresolved(
  token: string,
  chainId: number
): Promise<MasterLabelUnresolvedChainSnapshot> {
  return forgeRequest<MasterLabelUnresolvedChainSnapshot>(
    token,
    `${FORGE_DASHBOARDS_API_URL}/master-label-unresolved/refresh`,
    'Error refreshing Master Label unresolved snapshot',
    {
      method: 'POST',
      body: {
        chain_id: chainId,
      },
    }
  );
}

export async function fetchMasterLabelUnresolvedWorkItems(
  token: string,
  params?: {
    chainId?: number | null;
    unresolvedFilter?: MasterLabelUnresolvedFilter;
    page?: number;
    limit?: number;
    signal?: AbortSignal;
  }
): Promise<MasterLabelUnresolvedWorkItemsResponse> {
  const query = new URLSearchParams();
  if (params?.chainId) query.set('chain_id', String(params.chainId));
  if (params?.unresolvedFilter) query.set('unresolved_filter', params.unresolvedFilter);
  if (params?.page !== undefined) query.set('page', String(params.page));
  if (params?.limit !== undefined) query.set('limit', String(params.limit));
  return forgeRequest<MasterLabelUnresolvedWorkItemsResponse>(
    token,
    `${FORGE_MASTER_LABEL_UNRESOLVED_API_URL}/work-items${query.toString() ? `?${query.toString()}` : ''}`,
    'Error fetching Master Label unresolved work items',
    { signal: params?.signal }
  );
}

export async function exportMasterLabelUnresolvedWorkItems(
  token: string,
  params?: { chainId?: number | null; unresolvedFilter?: MasterLabelUnresolvedFilter }
): Promise<{ blob: Blob; filename: string }> {
  const query = new URLSearchParams();
  if (params?.chainId) query.set('chain_id', String(params.chainId));
  if (params?.unresolvedFilter) query.set('unresolved_filter', params.unresolvedFilter);
  return forgeBlobRequest(
    token,
    `${FORGE_MASTER_LABEL_UNRESOLVED_API_URL}/work-items/export${query.toString() ? `?${query.toString()}` : ''}`,
    'Error exporting Master Label unresolved work items',
    'master-label-unresolved-work-items.csv'
  );
}

export async function fetchCleaningDashboard(token: string): Promise<CleaningDashboardResponse> {
  return forgeRequest<CleaningDashboardResponse>(
    token,
    `${FORGE_CLEANING_API_URL}/dashboard`,
    'Error fetching cleaning tasks dashboard'
  );
}

export async function fetchCleaningItems(
  token: string,
  params: CleaningItemsQueryParams
): Promise<CleaningItemsResponse> {
  const query = buildCleaningItemsQuery(params);
  return normalizeCleaningItemsResponse(
    await forgeRequest<CleaningItemsWireResponse>(
      token,
      `${FORGE_CLEANING_API_URL}/items?${query.toString()}`,
      'Error fetching cleaning tasks items'
    )
  );
}

export async function exportCleaningItems(
  token: string,
  params: CleaningItemsQueryParams
): Promise<{ blob: Blob; filename: string }> {
  const query = buildCleaningItemsQuery(params);
  return forgeBlobRequest(
    token,
    `${FORGE_CLEANING_API_URL}/items/export?${query.toString()}`,
    'Error exporting cleaning tasks items',
    `cleaning-items-chain-${params.chainId}-${params.family ?? 'combined'}.csv`
  );
}

export async function setCleaningItemExcluded(
  token: string,
  chainId: number,
  itemKey: string,
  excluded: boolean
): Promise<{ chain_id: number; item_key: string; excluded: boolean }> {
  return forgeRequest<{ chain_id: number; item_key: string; excluded: boolean }>(
    token,
    `${FORGE_CLEANING_API_URL}/exclusions`,
    'Error updating cleaning item exclusion',
    {
      method: 'PUT',
      body: {
        chain_id: chainId,
        item_key: itemKey,
        excluded,
      },
    }
  );
}

/** Completed refresh runs for one chain (oldest first); null on 404 (old API). */
export async function fetchCleaningChainHistory(
  token: string,
  chainId: number,
  limit = 60
): Promise<CleaningChainHistoryResponse | null> {
  const response = await fetch(
    `${FORGE_CLEANING_API_URL}/chains/${chainId}/history?limit=${limit}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (response.status === 404) return null;
  ApiService.handleError(response);
  if (!response.ok) {
    throw await parseApiError(response, 'Error fetching cleaning tasks chain history');
  }
  return response.json();
}

/** Per-subscore aggregates for one chain; null when no snapshot is cached (404). */
export async function fetchCleaningChainStats(
  token: string,
  chainId: number
): Promise<CleaningChainStatsResponse | null> {
  const response = await fetch(`${FORGE_CLEANING_API_URL}/chains/${chainId}/stats`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (response.status === 404) return null;
  ApiService.handleError(response);
  if (!response.ok) {
    throw await parseApiError(response, 'Error fetching cleaning tasks chain stats');
  }
  return response.json();
}

export async function refreshCleaningChain(
  token: string,
  chainId: number
): Promise<CleaningRefreshRun> {
  return forgeRequest<CleaningRefreshRun>(
    token,
    `${FORGE_CLEANING_API_URL}/refresh`,
    'Error refreshing cleaning tasks chain',
    {
      method: 'POST',
      body: {
        chain_id: chainId,
      },
    }
  );
}

export async function fetchCleaningRun(token: string, runId: string): Promise<CleaningRefreshRun> {
  return forgeRequest<CleaningRefreshRun>(
    token,
    `${FORGE_CLEANING_API_URL}/runs/${encodeURIComponent(runId)}`,
    'Error fetching cleaning tasks refresh run'
  );
}

export async function fetchRemoteDataCollection(
  token: string
): Promise<RemoteDataCollectionDashboardResponse> {
  return forgeRequest<RemoteDataCollectionDashboardResponse>(
    token,
    `${FORGE_DASHBOARDS_API_URL}/remote-data-collection`,
    'Error fetching remote data collection dashboard',
    { dedupeKey: ['fetchRemoteDataCollection', token] }
  );
}

export async function refreshRemoteDataCollection(
  token: string,
  request: RemoteDataCollectionRefreshRequest
): Promise<RemoteDataCollectionChainSnapshot> {
  return forgeRequest<RemoteDataCollectionChainSnapshot>(
    token,
    `${FORGE_DASHBOARDS_API_URL}/remote-data-collection/refresh`,
    'Error refreshing remote data collection snapshot',
    { method: 'POST', body: request }
  );
}

export async function fetchRemoteDataCollectionConfig(
  token: string,
  params?: { nonAdminPreview?: boolean }
): Promise<RemoteDataCollectionDefaults> {
  const requestKey = `${token}:${params?.nonAdminPreview ? 'non-admin-preview' : 'default'}`;
  const query = new URLSearchParams();
  if (params?.nonAdminPreview) query.set('non_admin_preview', 'true');
  return forgeRequest<RemoteDataCollectionDefaults>(
    token,
    `${FORGE_REMOTE_DATA_COLLECTION_API_URL}/config${query.toString() ? `?${query.toString()}` : ''}`,
    'Error fetching Remote DC configuration',
    { dedupeKey: ['fetchRemoteDataCollectionConfig', requestKey] }
  );
}

export async function fetchRemoteDataCollectionConfigDefaults(
  token: string
): Promise<RemoteDataCollectionConfig> {
  return forgeRequest<RemoteDataCollectionConfig>(
    token,
    `${FORGE_REMOTE_DATA_COLLECTION_API_URL}/config/defaults`,
    'Error fetching Remote DC configuration'
  );
}

export async function updateRemoteDataCollectionConfigDefaults(
  token: string,
  request: RemoteDataCollectionConfigUpdateRequest
): Promise<RemoteDataCollectionConfig> {
  return forgeRequest<RemoteDataCollectionConfig>(
    token,
    `${FORGE_REMOTE_DATA_COLLECTION_API_URL}/config/defaults`,
    'Error updating Remote DC configuration',
    { method: 'PUT', body: request }
  );
}

export async function fetchRemoteDataCollectionModCategories(
  token: string,
  chainId: number
): Promise<RemoteDataCollectionCategoryOptionsResponse> {
  const query = new URLSearchParams({ chain_id: String(chainId) });
  return forgeRequest<RemoteDataCollectionCategoryOptionsResponse>(
    token,
    `${FORGE_REMOTE_DATA_COLLECTION_API_URL}/mod-categories?${query.toString()}`,
    'Error fetching Remote DC Mod categories'
  );
}

export async function fetchRemoteDataCollectionPresets(
  token: string
): Promise<RemoteDataCollectionPresetsResponse> {
  return forgeRequest<RemoteDataCollectionPresetsResponse>(
    token,
    `${FORGE_REMOTE_DATA_COLLECTION_API_URL}/presets`,
    'Error fetching Remote DC presets'
  );
}

export async function createRemoteDataCollectionPreset(
  token: string,
  request: RemoteDataCollectionPresetCreateRequest
): Promise<RemoteDataCollectionChainPresets> {
  return forgeRequest<RemoteDataCollectionChainPresets>(
    token,
    `${FORGE_REMOTE_DATA_COLLECTION_API_URL}/presets`,
    'Error creating Remote DC preset',
    { method: 'POST', body: request }
  );
}

export async function updateRemoteDataCollectionPreset(
  token: string,
  presetId: string,
  request: RemoteDataCollectionPresetUpdateRequest
): Promise<RemoteDataCollectionChainPresets> {
  return forgeRequest<RemoteDataCollectionChainPresets>(
    token,
    `${FORGE_REMOTE_DATA_COLLECTION_API_URL}/presets/${encodeURIComponent(presetId)}`,
    'Error updating Remote DC preset',
    { method: 'PUT', body: request }
  );
}

export async function deleteRemoteDataCollectionPreset(
  token: string,
  presetId: string,
  request: RemoteDataCollectionPresetDeleteRequest
): Promise<RemoteDataCollectionChainPresets> {
  return forgeRequest<RemoteDataCollectionChainPresets>(
    token,
    `${FORGE_REMOTE_DATA_COLLECTION_API_URL}/presets/${encodeURIComponent(presetId)}`,
    'Error deleting Remote DC preset',
    { method: 'DELETE', body: request }
  );
}

export async function updateRemoteDataCollectionDefaultPreset(
  token: string,
  request: RemoteDataCollectionDefaultPresetUpdateRequest
): Promise<RemoteDataCollectionChainPresets> {
  return forgeRequest<RemoteDataCollectionChainPresets>(
    token,
    `${FORGE_REMOTE_DATA_COLLECTION_API_URL}/default-preset`,
    'Error selecting the default Remote DC preset',
    { method: 'PUT', body: request }
  );
}

export async function exportRemoteDataCollectionWorkItems(
  token: string,
  params: { chainId: number; taskPrefix?: string | null }
): Promise<{ blob: Blob; filename: string }> {
  const query = new URLSearchParams();
  query.set('chain_id', String(params.chainId));
  if (params?.taskPrefix) query.set('task_prefix', params.taskPrefix);
  const response = await forgeFetch(
    token,
    `${FORGE_REMOTE_DATA_COLLECTION_API_URL}/work-items/export${query.toString() ? `?${query.toString()}` : ''}`,
    'Error exporting Remote DC work items'
  );
  // The export filename is deliberately fixed, ignoring content-disposition.
  return {
    blob: await response.blob(),
    filename: `Remote DC ${params.chainId}.csv`,
  };
}

export async function fetchDesignsDashboard(
  token: string,
  filters?: {
    chainId?: number | null;
    barcodeStatus?: BarcodeStatus | null;
    query?: string | null;
    updatedBy?: string | null;
    updatedByValues?: string[] | null;
    createdByValues?: string[] | null;
    groupByNormalizedBarcode?: boolean;
    reviewSort?: ForgeReviewSort | null;
    page?: number;
    limit?: number;
  }
): Promise<ForgeDesignsDashboardResponse> {
  const params = new URLSearchParams();
  if (filters?.chainId) params.set('chain_id', String(filters.chainId));
  if (filters?.barcodeStatus) params.set('barcode_status', filters.barcodeStatus);
  if (filters?.query) params.set('query', filters.query);
  if (filters?.updatedBy) params.set('updated_by', filters.updatedBy);
  filters?.updatedByValues?.forEach((value) => params.append('updated_by_values', value));
  filters?.createdByValues?.forEach((value) => params.append('created_by_values', value));
  if (filters?.groupByNormalizedBarcode) params.set('group_by_normalized_barcode', 'true');
  if (filters?.reviewSort) params.set('review_sort', filters.reviewSort);
  if (filters?.page) params.set('page', String(filters.page));
  if (filters?.limit) params.set('limit', String(filters.limit));
  const query = params.toString();
  const requestKey = `${token}:${query}`;
  return forgeRequest<ForgeDesignsDashboardResponse>(
    token,
    `${FORGE_DASHBOARDS_API_URL}/designs${query ? `?${query}` : ''}`,
    'Error fetching Forge designs dashboard',
    { dedupeKey: ['fetchDesignsDashboard', requestKey] }
  );
}

export async function refreshDesignsDashboard(
  token: string,
  request: ForgeDesignsRefreshRequest
): Promise<ForgeDesignsDashboardResponse> {
  return forgeRequest<ForgeDesignsDashboardResponse>(
    token,
    `${FORGE_DASHBOARDS_API_URL}/designs/refresh`,
    'Error refreshing Forge designs dashboard',
    { method: 'POST', body: request }
  );
}

export async function updateDesignReviewStatus(
  token: string,
  designId: string,
  request: ForgeReviewStatusUpdateRequest
): Promise<DesignResponse> {
  return forgeRequest<DesignResponse>(
    token,
    `${FORGE_DESIGNS_API_URL}/${designId}/review`,
    'Error updating Forge design review status',
    { method: 'PATCH', body: request }
  );
}

export async function updateDesignReviewStatusByNormalizedBarcode(
  token: string,
  request: ForgeBarcodeReviewStatusUpdateRequest
): Promise<ForgeBarcodeReviewStatusUpdateResponse> {
  return forgeRequest<ForgeBarcodeReviewStatusUpdateResponse>(
    token,
    `${FORGE_DESIGNS_API_URL}/review/by-normalized-barcode`,
    'Error updating Forge Design review status',
    { method: 'PATCH', body: request }
  );
}

export async function fetchDesignLocks(
  token: string,
  filters: FetchDesignLocksRequest = {}
): Promise<DesignLockListResponse> {
  const params = new URLSearchParams();
  if (filters.chainId !== undefined && filters.chainId !== null) {
    params.set('chain_id', String(filters.chainId));
  }
  if (filters.status) params.set('status', filters.status);
  const search = filters.search?.trim();
  if (search) params.set('search', search);
  if (filters.page !== undefined) params.set('page', String(filters.page));
  if (filters.pageSize !== undefined) params.set('page_size', String(filters.pageSize));
  const query = params.toString();
  const payload = await forgeRequest<ApiDesignLockListResponse | ApiDesignLockListEntry[]>(
    token,
    `${FORGE_DESIGN_LOCKS_API_URL}${query ? `?${query}` : ''}`,
    'Error fetching design locks'
  );
  if (Array.isArray(payload)) {
    return {
      locks: payload.map(mapDesignLockListEntry),
      total: payload.length,
      page: filters.page ?? 1,
      pageSize: filters.pageSize ?? payload.length,
    };
  }
  return {
    locks: payload.locks.map(mapDesignLockListEntry),
    total: payload.total,
    page: payload.page,
    pageSize: payload.pageSize,
  };
}

export async function fetchItemModPrefixes(
  token: string,
  chainId: number,
  itemNumbers: string[]
): Promise<ItemModPrefixesResponse> {
  const query = new URLSearchParams({ chain_id: String(chainId) });
  itemNumbers.forEach((itemNumber) => query.append('item_number', itemNumber));
  return forgeRequest<ItemModPrefixesResponse>(
    token,
    `${FORGE_ADMIN_API_URL}/item-mod-prefixes?${query.toString()}`,
    'Error fetching item mod prefixes'
  );
}

export async function setItemModPrefix(
  token: string,
  chainId: number,
  itemNumber: string,
  modPrefix: string
): Promise<ItemModPrefixEntry> {
  return forgeRequest<ItemModPrefixEntry>(
    token,
    `${FORGE_ADMIN_API_URL}/item-mod-prefixes`,
    'Error saving item mod prefix',
    { method: 'PUT', body: { chain_id: chainId, item_number: itemNumber, mod_prefix: modPrefix } }
  );
}

export async function fetchDesignLockSummary(token: string): Promise<DesignLockSummaryResponse> {
  return forgeRequest<DesignLockSummaryResponse>(
    token,
    `${FORGE_DESIGN_LOCKS_API_URL}/summary`,
    'Error fetching design lock summary'
  );
}

export async function createDesignLock(
  token: string,
  request: CreateDesignLockRequest
): Promise<DesignLockMutationResponse> {
  return mapDesignLockStatusInfo(
    await forgeRequest<ApiDesignLockStatusInfo>(
      token,
      FORGE_DESIGN_LOCKS_API_URL,
      'Error creating design lock',
      { method: 'POST', body: request }
    )
  );
}

export async function updateDesignLock(
  token: string,
  designId: string,
  chainId: number
): Promise<DesignLockMutationResponse> {
  const params = new URLSearchParams({ chain_id: String(chainId) });
  return mapDesignLockStatusInfo(
    await forgeRequest<ApiDesignLockStatusInfo>(
      token,
      `${FORGE_DESIGN_LOCKS_API_URL}/${encodeURIComponent(designId)}?${params}`,
      'Error updating design lock',
      { method: 'PUT' }
    )
  );
}

export async function deleteDesignLock(
  token: string,
  designId: string,
  chainId: number
): Promise<void> {
  const params = new URLSearchParams({ chain_id: String(chainId) });
  return forgeVoidRequest(
    token,
    `${FORGE_DESIGN_LOCKS_API_URL}/${encodeURIComponent(designId)}?${params}`,
    'Error deleting design lock',
    { method: 'DELETE' }
  );
}

export async function fetchDesignLock(
  token: string,
  designId: string,
  filters: { chainId: number; includeCrops?: boolean }
): Promise<DesignLockDetailResponse> {
  const params = new URLSearchParams({
    chain_id: String(filters.chainId),
    include_crops: String(filters.includeCrops ?? false),
  });
  return mapDesignLockListEntry(
    await forgeRequest<ApiDesignLockListEntry>(
      token,
      `${FORGE_DESIGN_LOCKS_API_URL}/${encodeURIComponent(designId)}?${params}`,
      'Error fetching design lock'
    )
  );
}

export async function fetchVizSimDatasets(
  token: string,
  filters?: {
    chainId?: number | null;
    page?: number;
    limit?: number;
  }
): Promise<VizSimDatasetListResponse> {
  const params = new URLSearchParams();
  if (filters?.chainId) params.set('chain_id', String(filters.chainId));
  if (filters?.page) params.set('page', String(filters.page));
  if (filters?.limit) params.set('limit', String(filters.limit));
  const query = params.toString();
  return forgeRequest<VizSimDatasetListResponse>(
    token,
    `${FORGE_VIZSIM_DATASETS_API_URL}${query ? `?${query}` : ''}`,
    'Error fetching VizSim datasets'
  );
}

export async function fetchVizSimDataset(
  token: string,
  datasetId: string,
  filters?: {
    releaseMajor?: number | null;
    releaseMinor?: number | null;
    label?: string | null;
    tag?: string | null;
    classHash?: string | null;
    samplePage?: number;
    sampleLimit?: number;
  }
): Promise<VizSimDatasetDetailResponse> {
  const params = new URLSearchParams();
  if (filters?.releaseMajor !== undefined && filters.releaseMajor !== null) {
    params.set('release_major', String(filters.releaseMajor));
  }
  if (filters?.releaseMinor !== undefined && filters.releaseMinor !== null) {
    params.set('release_minor', String(filters.releaseMinor));
  }
  if (filters?.label) params.set('label', filters.label);
  if (filters?.tag) params.set('tag', filters.tag);
  if (filters?.classHash) params.set('class_hash', filters.classHash);
  if (filters?.samplePage) params.set('sample_page', String(filters.samplePage));
  if (filters?.sampleLimit) params.set('sample_limit', String(filters.sampleLimit));
  const query = params.toString();
  return forgeRequest<VizSimDatasetDetailResponse>(
    token,
    `${FORGE_VIZSIM_DATASETS_API_URL}/${encodeURIComponent(datasetId)}${query ? `?${query}` : ''}`,
    'Error fetching VizSim dataset'
  );
}

export async function createVizSimDataset(
  token: string,
  request: EnsureVizSimDatasetRequest
): Promise<VizSimDatasetDetailResponse> {
  return forgeRequest<VizSimDatasetDetailResponse>(
    token,
    FORGE_VIZSIM_DATASETS_API_URL,
    'Error creating VizSim dataset',
    { method: 'POST', body: request }
  );
}

export async function addVizSimDatasetCrops(
  token: string,
  datasetId: string,
  request: AddVizSimDatasetCropsRequest
): Promise<AddVizSimDatasetCropsResponse> {
  return forgeRequest<AddVizSimDatasetCropsResponse>(
    token,
    `${FORGE_VIZSIM_DATASETS_API_URL}/${encodeURIComponent(datasetId)}/crops`,
    'Error adding crops to VizSim dataset',
    { method: 'POST', body: request }
  );
}

export async function fetchVizSimDatasetTagSuggestions(
  token: string,
  request: VizSimDatasetTagSuggestionsRequest
): Promise<VizSimDatasetTagSuggestionsResponse> {
  return forgeRequest<VizSimDatasetTagSuggestionsResponse>(
    token,
    `${FORGE_VIZSIM_DATASETS_API_URL}/tag-suggestions`,
    'Error fetching VizSim tag suggestions',
    { method: 'POST', body: request }
  );
}

export async function addVizSimDatasetChainCrops(
  token: string,
  chainId: number,
  request: AddVizSimDatasetCropsRequest
): Promise<AddVizSimDatasetCropsResponse> {
  return forgeRequest<AddVizSimDatasetCropsResponse>(
    token,
    `${FORGE_VIZSIM_DATASETS_API_URL}/chains/${encodeURIComponent(String(chainId))}/crops`,
    'Error adding crops to VizSim dataset',
    { method: 'POST', body: request }
  );
}

export async function updateVizSimDatasetCrop(
  token: string,
  datasetId: string,
  cropId: string,
  request: UpdateVizSimDatasetCropRequest
): Promise<VizSimDatasetCrop> {
  return forgeRequest<VizSimDatasetCrop>(
    token,
    `${FORGE_VIZSIM_DATASETS_API_URL}/${encodeURIComponent(datasetId)}/crops/${encodeURIComponent(
      cropId
    )}`,
    'Error updating VizSim dataset crop',
    { method: 'PATCH', body: request }
  );
}

export async function removeVizSimDatasetCrop(
  token: string,
  datasetId: string,
  cropId: string,
  scope: 'crop' | 'class'
): Promise<RemoveVizSimDatasetCropsResponse> {
  const params = new URLSearchParams({ scope });
  return forgeRequest<RemoveVizSimDatasetCropsResponse>(
    token,
    `${FORGE_VIZSIM_DATASETS_API_URL}/${encodeURIComponent(datasetId)}/crops/${encodeURIComponent(
      cropId
    )}?${params.toString()}`,
    'Error removing VizSim dataset crop',
    { method: 'DELETE' }
  );
}

export async function releaseVizSimDataset(
  token: string,
  datasetId: string,
  request: ReleaseVizSimDatasetRequest
): Promise<VizSimDatasetDetailResponse> {
  return forgeRequest<VizSimDatasetDetailResponse>(
    token,
    `${FORGE_VIZSIM_DATASETS_API_URL}/${encodeURIComponent(datasetId)}/releases`,
    'Error releasing VizSim dataset',
    { method: 'POST', body: request }
  );
}

export async function fetchReleasedVizSimCropIds(
  token: string,
  filters?: {
    chainId?: number | null;
    datasetId?: string | null;
    label?: string | null;
    tag?: string | null;
  }
): Promise<ReleasedVizSimCropIdsResponse> {
  const params = new URLSearchParams();
  if (filters?.chainId) params.set('chain_id', String(filters.chainId));
  if (filters?.datasetId) params.set('dataset_id', filters.datasetId);
  if (filters?.label) params.set('label', filters.label);
  if (filters?.tag) params.set('tag', filters.tag);
  const query = params.toString();
  return forgeRequest<ReleasedVizSimCropIdsResponse>(
    token,
    `${FORGE_VIZSIM_DATASETS_API_URL}/released-crop-ids${query ? `?${query}` : ''}`,
    'Error fetching released VizSim crop ids'
  );
}

export async function fetchDesignGroupsDashboard(
  token: string,
  filters?: {
    chainId?: number | null;
    scope?: string | null;
    query?: string | null;
    updatedBy?: string | null;
    updatedByValues?: string[] | null;
    reviewSort?: ForgeReviewSort | null;
    page?: number;
    limit?: number;
  }
): Promise<ForgeDesignGroupsDashboardResponse> {
  const params = new URLSearchParams();
  if (filters?.chainId) params.set('chain_id', String(filters.chainId));
  if (filters?.scope) params.set('scope', filters.scope);
  if (filters?.query) params.set('query', filters.query);
  if (filters?.updatedBy) params.set('updated_by', filters.updatedBy);
  filters?.updatedByValues?.forEach((value) => params.append('updated_by_values', value));
  if (filters?.reviewSort) params.set('review_sort', filters.reviewSort);
  if (filters?.page) params.set('page', String(filters.page));
  if (filters?.limit) params.set('limit', String(filters.limit));
  const query = params.toString();
  const requestKey = `${token}:${query}`;
  return forgeRequest<ForgeDesignGroupsDashboardResponse>(
    token,
    `${FORGE_DASHBOARDS_API_URL}/design-groups${query ? `?${query}` : ''}`,
    'Error fetching Forge design groups dashboard',
    { dedupeKey: ['fetchDesignGroupsDashboard', requestKey] }
  );
}

export async function fetchReviewIssuesDashboard(
  token: string,
  filters?: {
    chainId?: number | null;
    issueType?: MasterLabelReviewIssueType | null;
    assignmentFilter?: ForgeReviewIssueAssignmentFilter | null;
    activeFilter?: ForgeReviewIssueActiveFilter | null;
    query?: string | null;
    updatedByValues?: string[] | null;
    sort?: ForgeReviewIssueSort | null;
    page?: number;
    limit?: number;
  }
): Promise<ForgeReviewIssuesDashboardResponse> {
  const params = new URLSearchParams();
  if (filters?.chainId) params.set('chain_id', String(filters.chainId));
  if (filters?.issueType) params.set('issue_type', filters.issueType);
  if (filters?.assignmentFilter) params.set('assignment_filter', filters.assignmentFilter);
  if (filters?.activeFilter) params.set('active_filter', filters.activeFilter);
  if (filters?.query) params.set('query', filters.query);
  filters?.updatedByValues?.forEach((value) => params.append('updated_by_values', value));
  if (filters?.sort) params.set('sort', filters.sort);
  if (filters?.page) params.set('page', String(filters.page));
  if (filters?.limit) params.set('limit', String(filters.limit));
  const query = params.toString();
  const requestKey = `${token}:${query}`;
  return forgeRequest<ForgeReviewIssuesDashboardResponse>(
    token,
    `${FORGE_DASHBOARDS_API_URL}/review-issues${query ? `?${query}` : ''}`,
    'Error fetching Forge review issues dashboard',
    { dedupeKey: ['fetchReviewIssuesDashboard', requestKey] }
  );
}

export async function refreshReviewIssuesDashboard(
  token: string,
  chainId: number
): Promise<ForgeReviewIssuesChainSnapshot> {
  return forgeRequest<ForgeReviewIssuesChainSnapshot>(
    token,
    `${FORGE_DASHBOARDS_API_URL}/review-issues/refresh`,
    'Error refreshing Forge review issues snapshot',
    {
      method: 'POST',
      body: {
        chain_id: chainId,
      },
    }
  );
}

export async function deleteCrops(
  token: string,
  cropIds: string[]
): Promise<CropLifecycleResponse> {
  return forgeRequest<CropLifecycleResponse>(token, FORGE_CROPS_API_URL, 'Error deleting crops', {
    method: 'DELETE',
    body: { crop_ids: cropIds },
  });
}

export async function refreshDesignGroupsDashboard(
  token: string,
  request: ForgeDesignGroupsRefreshRequest
): Promise<ForgeDesignGroupsDashboardResponse> {
  return forgeRequest<ForgeDesignGroupsDashboardResponse>(
    token,
    `${FORGE_DASHBOARDS_API_URL}/design-groups/refresh`,
    'Error refreshing Forge design groups dashboard',
    { method: 'POST', body: request }
  );
}

export async function updateDesignGroupScope(
  token: string,
  groupId: string,
  request: ForgeDesignGroupsScopeChangeRequest
): Promise<ForgeDesignGroupsDashboardResponse> {
  return forgeRequest<ForgeDesignGroupsDashboardResponse>(
    token,
    `${FORGE_DASHBOARDS_API_URL}/design-groups/${groupId}/scope`,
    'Error changing Forge design group type',
    { method: 'PATCH', body: request }
  );
}

export async function updateDesignGroupName(
  token: string,
  groupId: string,
  name: string | null
): Promise<ForgeDesignGroupNameUpdateResponse> {
  return forgeRequest<ForgeDesignGroupNameUpdateResponse>(
    token,
    `${FORGE_DESIGN_GROUPS_API_URL}/${groupId}/name`,
    'Error updating Forge design group name',
    { method: 'PATCH', body: { name } }
  );
}

export async function updateDesignGroupReviewStatus(
  token: string,
  groupId: string,
  request: ForgeReviewStatusUpdateRequest
): Promise<ForgeReviewStatusUpdateResponse> {
  return normalizeReviewStatusUpdateResponse(
    await forgeRequest<ApiReviewStatusUpdateResponse>(
      token,
      `${FORGE_DESIGN_GROUPS_API_URL}/${groupId}/review`,
      'Error updating Forge design group review status',
      { method: 'PATCH', body: request }
    ),
    groupId,
    request.isReviewed
  );
}

export async function fetchSystemHealth(
  token: string,
  chainId?: number | null
): Promise<ForgeSystemHealthDashboardResponse> {
  const requestKey = `${token}:${chainId ?? 'all'}`;
  const params = new URLSearchParams();
  if (chainId) params.set('chain_id', String(chainId));
  const query = params.toString();
  return forgeRequest<ForgeSystemHealthDashboardResponse>(
    token,
    `${FORGE_DASHBOARDS_API_URL}/system-health${query ? `?${query}` : ''}`,
    'Error fetching Forge system health dashboard',
    { dedupeKey: ['fetchSystemHealth', requestKey] }
  );
}

export async function createSystemHealthRefreshRun(
  token: string,
  request: ForgeSystemHealthRefreshRequest
): Promise<ForgeSystemHealthRefreshRunResponse> {
  return forgeRequest<ForgeSystemHealthRefreshRunResponse>(
    token,
    `${FORGE_DASHBOARDS_API_URL}/system-health/runs`,
    'Error starting Forge system health refresh',
    { method: 'POST', body: request }
  );
}

export async function fetchSystemHealthRefreshRun(
  token: string,
  runId: string,
  params?: { signal?: AbortSignal }
): Promise<ForgeSystemHealthRefreshRunResponse> {
  return forgeRequest<ForgeSystemHealthRefreshRunResponse>(
    token,
    `${FORGE_DASHBOARDS_API_URL}/system-health/runs/${encodeURIComponent(runId)}`,
    'Error fetching Forge system health refresh',
    { signal: params?.signal }
  );
}

export async function createRemoteDataCollectionRun(
  token: string,
  request: RemoteDataCollectionRefreshRequest
): Promise<RemoteDataCollectionRunResponse> {
  return forgeRequest<RemoteDataCollectionRunResponse>(
    token,
    `${FORGE_REMOTE_DATA_COLLECTION_API_URL}/runs`,
    'Error creating remote data collection run',
    { method: 'POST', body: request }
  );
}

export async function fetchRemoteDataCollectionRun(
  token: string,
  runId: string,
  params?: { signal?: AbortSignal }
): Promise<RemoteDataCollectionRunResponse> {
  return forgeRequest<RemoteDataCollectionRunResponse>(
    token,
    `${FORGE_REMOTE_DATA_COLLECTION_API_URL}/runs/${encodeURIComponent(runId)}`,
    'Error fetching remote data collection run',
    { signal: params?.signal }
  );
}

export async function fetchMasterDataCollectionInference(
  token: string,
  inferenceId: string,
  params?: {
    chainId?: number | null;
    itemNumber?: string | null;
    normalizedBarcode?: string | null;
    similarityThreshold?: number;
    deltaThreshold?: number;
    mergeLinkedCandidates?: boolean;
    nonAdminPreview?: boolean;
    signal?: AbortSignal;
  }
): Promise<MasterDataCollectionDetailResponse> {
  const query = new URLSearchParams();
  if (params?.chainId) query.set('chain_id', String(params.chainId));
  if (params?.itemNumber) query.set('item_number', params.itemNumber);
  if (params?.normalizedBarcode) query.set('normalized_barcode', params.normalizedBarcode);
  if (params?.similarityThreshold !== undefined) {
    query.set('similarity_threshold', String(params.similarityThreshold));
  }
  if (params?.deltaThreshold !== undefined) {
    query.set('delta_threshold', String(params.deltaThreshold));
  }
  if (params?.mergeLinkedCandidates !== undefined) {
    query.set('merge_linked_candidates', String(params.mergeLinkedCandidates));
  }
  if (params?.nonAdminPreview) query.set('non_admin_preview', 'true');
  return forgeRequest<MasterDataCollectionDetailResponse>(
    token,
    `${FORGE_MASTER_DATA_COLLECTION_API_URL}/inferences/${encodeURIComponent(
      inferenceId
    )}${query.toString() ? `?${query.toString()}` : ''}`,
    'Error fetching Master Data Collection inference',
    { signal: params?.signal }
  );
}

export async function fetchMasterDataCollectionReviewTargets(
  token: string,
  params: {
    chainId: number;
    itemNumber?: string | null;
    normalizedBarcode?: string | null;
    maxStores?: number;
  }
): Promise<RemoteDataCollectionReviewTargetsResponse> {
  const query = new URLSearchParams();
  query.set('chain_id', String(params.chainId));
  if (params.itemNumber) query.set('item_number', params.itemNumber);
  if (params.normalizedBarcode) query.set('normalized_barcode', params.normalizedBarcode);
  if (params.maxStores !== undefined) query.set('max_stores', String(params.maxStores));

  return forgeRequest<RemoteDataCollectionReviewTargetsResponse>(
    token,
    `${FORGE_MASTER_DATA_COLLECTION_API_URL}/review-targets?${query.toString()}`,
    'Error fetching Remote DC review targets'
  );
}

export async function fetchMasterDataCollectionImagePreview(
  token: string,
  imageId: number,
  params?: {
    chainId?: number | null;
    itemNumber?: string | null;
    normalizedBarcode?: string | null;
    signal?: AbortSignal;
  }
): Promise<MasterDataCollectionImagePreviewResponse> {
  const query = new URLSearchParams();
  if (params?.chainId) query.set('chain_id', String(params.chainId));
  if (params?.itemNumber) query.set('item_number', params.itemNumber);
  if (params?.normalizedBarcode) query.set('normalized_barcode', params.normalizedBarcode);

  return forgeRequest<MasterDataCollectionImagePreviewResponse>(
    token,
    `${FORGE_MASTER_DATA_COLLECTION_API_URL}/images/${encodeURIComponent(
      String(imageId)
    )}/preview${query.toString() ? `?${query.toString()}` : ''}`,
    'Error fetching Remote DC image preview',
    { signal: params?.signal }
  );
}

export async function fetchMasterDataCollectionImageTimeline(
  token: string,
  imageId: number,
  params?: {
    chainId?: number | null;
    itemNumber?: string | null;
    normalizedBarcode?: string | null;
    previousCount?: number;
    signal?: AbortSignal;
  }
): Promise<RemoteDataCollectionImageTimelineResponse> {
  const query = new URLSearchParams();
  if (params?.chainId) query.set('chain_id', String(params.chainId));
  if (params?.itemNumber) query.set('item_number', params.itemNumber);
  if (params?.normalizedBarcode) query.set('normalized_barcode', params.normalizedBarcode);
  if (params?.previousCount !== undefined)
    query.set('previous_count', String(params.previousCount));

  return forgeRequest<RemoteDataCollectionImageTimelineResponse>(
    token,
    `${FORGE_MASTER_DATA_COLLECTION_API_URL}/images/${encodeURIComponent(
      String(imageId)
    )}/timeline${query.toString() ? `?${query.toString()}` : ''}`,
    'Error fetching Remote DC image timeline',
    { signal: params?.signal }
  );
}

export async function loadMasterDataCollectionImageInference(
  token: string,
  imageId: number,
  params?: {
    chainId?: number | null;
    itemNumber?: string | null;
    normalizedBarcode?: string | null;
    similarityThreshold?: number;
    deltaThreshold?: number;
    mergeLinkedCandidates?: boolean;
    nonAdminPreview?: boolean;
    cachedOnly?: boolean;
    signal?: AbortSignal;
  }
): Promise<MasterDataCollectionDetailResponse> {
  const query = new URLSearchParams();
  if (params?.chainId) query.set('chain_id', String(params.chainId));
  if (params?.itemNumber) query.set('item_number', params.itemNumber);
  if (params?.normalizedBarcode) query.set('normalized_barcode', params.normalizedBarcode);
  if (params?.similarityThreshold !== undefined) {
    query.set('similarity_threshold', String(params.similarityThreshold));
  }
  if (params?.deltaThreshold !== undefined) {
    query.set('delta_threshold', String(params.deltaThreshold));
  }
  if (params?.mergeLinkedCandidates !== undefined) {
    query.set('merge_linked_candidates', String(params.mergeLinkedCandidates));
  }
  if (params?.nonAdminPreview) query.set('non_admin_preview', 'true');
  if (params?.cachedOnly !== undefined) query.set('cached_only', String(params.cachedOnly));
  return forgeRequest<MasterDataCollectionDetailResponse>(
    token,
    `${FORGE_MASTER_DATA_COLLECTION_API_URL}/images/${encodeURIComponent(
      String(imageId)
    )}/load-inference${query.toString() ? `?${query.toString()}` : ''}`,
    'Error loading Remote DC product recognition',
    { method: 'POST', signal: params?.signal }
  );
}

export async function fetchMasterDataCollectionDailyPreviews(
  token: string,
  imageId: number,
  params?: {
    chainId?: number | null;
    itemNumber?: string | null;
    normalizedBarcode?: string | null;
    daysBefore?: number;
    daysAfter?: number;
  }
): Promise<RemoteDataCollectionDailyPreviewsResponse> {
  const query = new URLSearchParams();
  if (params?.chainId) query.set('chain_id', String(params.chainId));
  if (params?.itemNumber) query.set('item_number', params.itemNumber);
  if (params?.normalizedBarcode) query.set('normalized_barcode', params.normalizedBarcode);
  if (params?.daysBefore !== undefined) query.set('days_before', String(params.daysBefore));
  if (params?.daysAfter !== undefined) query.set('days_after', String(params.daysAfter));

  return forgeRequest<RemoteDataCollectionDailyPreviewsResponse>(
    token,
    `${FORGE_MASTER_DATA_COLLECTION_API_URL}/images/${encodeURIComponent(
      String(imageId)
    )}/daily-previews${query.toString() ? `?${query.toString()}` : ''}`,
    'Error fetching Remote DC image previews'
  );
}

export async function fetchMasterLabelCropQuery(
  token: string,
  cropId: string,
  params?: {
    chainId?: number | null;
    limit?: number;
  }
): Promise<MasterLabelCropQueryResponse> {
  const query = new URLSearchParams();
  if (params?.chainId) query.set('chain_id', String(params.chainId));
  if (params?.limit !== undefined) query.set('limit', String(params.limit));

  return forgeRequest<MasterLabelCropQueryResponse>(
    token,
    `${FORGE_MASTER_LABEL_API_URL}/crops/${encodeURIComponent(
      cropId
    )}/query${query.toString() ? `?${query.toString()}` : ''}`,
    'Error querying Master Label crop'
  );
}

export async function saveMasterDataCollectionMasterLabelState(
  token: string,
  inferenceId: string,
  request: MasterDataCollectionMasterLabelStateSaveRequest
): Promise<MasterDataCollectionMasterLabelStateSaveResponse> {
  return forgeRequest<MasterDataCollectionMasterLabelStateSaveResponse>(
    token,
    `${FORGE_MASTER_DATA_COLLECTION_API_URL}/inferences/${encodeURIComponent(
      inferenceId
    )}/master-label-state`,
    'Error saving Master Data Collection Master Label state',
    { method: 'PUT', body: request }
  );
}

export async function fetchProductRecognitionInferenceDashboard(
  token: string,
  date: string,
  hour?: number | null,
  chainId?: number | null
): Promise<ProductRecognitionInferenceDashboardResponse> {
  const requestKey = `${token}:${date}:${hour ?? 'combined'}:${chainId ?? 'all'}`;
  const cachedResponse = productRecognitionInferenceDashboardCache.get(requestKey);
  if (cachedResponse) {
    return cachedResponse;
  }
  const existingRequest = inFlightProductRecognitionInferenceRequests.get(requestKey);
  if (existingRequest) {
    return existingRequest;
  }

  const params = new URLSearchParams({ date });
  if (hour !== null && hour !== undefined) params.set('hour', String(hour));
  if (chainId) params.set('chain_id', String(chainId));
  const cacheGeneration = productRecognitionInferenceCacheGeneration;
  const request = (async (): Promise<ProductRecognitionInferenceDashboardResponse> => {
    const body = await forgeRequest<ProductRecognitionInferenceDashboardResponse>(
      token,
      `${FORGE_DASHBOARDS_API_URL}/product-recognition-inference?${params.toString()}`,
      'Error fetching Product Recognition inference dashboard'
    );
    if (cacheGeneration === productRecognitionInferenceCacheGeneration) {
      productRecognitionInferenceDashboardCache.set(requestKey, body);
    }
    return body;
  })();

  inFlightProductRecognitionInferenceRequests.set(requestKey, request);
  try {
    return await request;
  } finally {
    inFlightProductRecognitionInferenceRequests.delete(requestKey);
  }
}

export async function fetchProductRecognitionInferenceAggregate(
  token: string,
  dateFrom: string,
  dateTo: string,
  hour?: number | null,
  chainId?: number | null,
  modPrefix?: string | null
): Promise<ProductRecognitionInferenceAggregateResponse> {
  const requestKey = `${token}:${dateFrom}:${dateTo}:${hour ?? 'combined'}:${chainId ?? 'all'}:${modPrefix ?? 'all'}`;
  const cachedResponse = productRecognitionInferenceAggregateCache.get(requestKey);
  if (cachedResponse) {
    return cachedResponse;
  }
  const existingRequest = inFlightProductRecognitionInferenceAggregateRequests.get(requestKey);
  if (existingRequest) {
    return existingRequest;
  }

  const params = new URLSearchParams({
    date_from: dateFrom,
    date_to: dateTo,
  });
  if (hour !== null && hour !== undefined) params.set('hour', String(hour));
  if (chainId) params.set('chain_id', String(chainId));
  if (modPrefix) params.set('mod_prefix', modPrefix);
  const cacheGeneration = productRecognitionInferenceCacheGeneration;
  const request = (async (): Promise<ProductRecognitionInferenceAggregateResponse> => {
    const body = await forgeRequest<ProductRecognitionInferenceAggregateResponse>(
      token,
      `${FORGE_DASHBOARDS_API_URL}/product-recognition-inference/aggregate?${params.toString()}`,
      'Error fetching Product Recognition inference aggregate'
    );
    if (cacheGeneration === productRecognitionInferenceCacheGeneration) {
      productRecognitionInferenceAggregateCache.set(requestKey, body);
    }
    return body;
  })();

  inFlightProductRecognitionInferenceAggregateRequests.set(requestKey, request);
  try {
    return await request;
  } finally {
    inFlightProductRecognitionInferenceAggregateRequests.delete(requestKey);
  }
}

export async function fetchProductRecognitionInferencePlotAnnotations(
  token: string,
  dateFrom: string,
  dateTo: string,
  chainId?: number | null
): Promise<ProductRecognitionInferencePlotAnnotationsResponse> {
  const requestKey = `${token}:${dateFrom}:${dateTo}:${chainId ?? 'all'}`;
  const existingRequest = inFlightProductRecognitionInferenceAnnotationRequests.get(requestKey);
  if (existingRequest) {
    return existingRequest;
  }

  const params = new URLSearchParams({
    date_from: dateFrom,
    date_to: dateTo,
  });
  if (chainId) params.set('chain_id', String(chainId));
  const request = forgeRequest<ProductRecognitionInferencePlotAnnotationsResponse>(
    token,
    `${FORGE_DASHBOARDS_API_URL}/product-recognition-inference/annotations?${params.toString()}`,
    'Error fetching Product Recognition inference daily notes'
  );

  inFlightProductRecognitionInferenceAnnotationRequests.set(requestKey, request);
  try {
    return await request;
  } finally {
    inFlightProductRecognitionInferenceAnnotationRequests.delete(requestKey);
  }
}

export async function saveProductRecognitionInferencePlotAnnotation(
  token: string,
  request: ProductRecognitionInferencePlotAnnotationRequest
): Promise<ProductRecognitionInferencePlotAnnotation> {
  return forgeRequest<ProductRecognitionInferencePlotAnnotation>(
    token,
    `${FORGE_DASHBOARDS_API_URL}/product-recognition-inference/annotations`,
    'Error saving Product Recognition inference daily note',
    {
      method: 'PUT',
      body: {
        selected_date: request.selectedDate,
        chain_id: request.chainId,
        note: request.note,
      },
    }
  );
}

export async function createProductRecognitionInferenceRefreshRun(
  token: string,
  request: ProductRecognitionInferenceRefreshRequest
): Promise<ProductRecognitionInferenceRefreshRunResponse> {
  const body = await forgeRequest<ProductRecognitionInferenceRefreshRunResponse>(
    token,
    `${FORGE_DASHBOARDS_API_URL}/product-recognition-inference/runs`,
    'Error starting Product Recognition inference refresh',
    {
      method: 'POST',
      body: {
        chain_id: request.chainId,
        date: request.date,
        hour: request.hour,
        hours: request.hours,
        similarity_threshold: request.similarityThreshold,
        delta_threshold: request.deltaThreshold,
        merge_linked_candidates: request.mergeLinkedCandidates,
      },
    }
  );
  if (body.status === 'completed') clearProductRecognitionInferenceCaches();
  return body;
}

export async function fetchProductRecognitionInferenceRefreshRun(
  token: string,
  runId: string,
  params?: { signal?: AbortSignal }
): Promise<ProductRecognitionInferenceRefreshRunResponse> {
  const body = await forgeRequest<ProductRecognitionInferenceRefreshRunResponse>(
    token,
    `${FORGE_DASHBOARDS_API_URL}/product-recognition-inference/runs/${encodeURIComponent(runId)}`,
    'Error fetching Product Recognition inference refresh',
    { signal: params?.signal }
  );
  if (body.status === 'completed') clearProductRecognitionInferenceCaches();
  return body;
}

export async function fetchItem(
  token: string,
  itemNumber: string,
  chainId: number
): Promise<ApiMasterLabelUPCData> {
  const params = new URLSearchParams({
    chain_id: String(chainId),
  });
  return forgeRequest<ApiMasterLabelUPCData>(
    token,
    `${FORGE_MASTER_LABEL_API_URL}/item-number/${encodeURIComponent(itemNumber)}?${params}`,
    'Error fetching item data'
  );
}

export async function fetchItemByBarcode(
  token: string,
  barcodeQuery: string,
  chainId: number
): Promise<ApiMasterLabelBarcodeLookupResponse> {
  const params = new URLSearchParams({
    chain_id: String(chainId),
  });
  return forgeRequest<ApiMasterLabelBarcodeLookupResponse>(
    token,
    `${FORGE_MASTER_LABEL_API_URL}/barcode/${encodeURIComponent(barcodeQuery)}?${params}`,
    'Error fetching item data by barcode'
  );
}

export async function fetchRecognitionReviewItem(
  token: string,
  itemNumber: string,
  chainId: number
): Promise<ApiMasterLabelUPCData> {
  const params = new URLSearchParams({ chain_id: String(chainId) });
  return forgeRequest<ApiMasterLabelUPCData>(
    token,
    `${FORGE_PR_REVIEW_API_URL}/catalog/item-number/${encodeURIComponent(itemNumber)}?${params}`,
    'Error fetching Recognition catalog evidence'
  );
}

export async function fetchRecognitionReviewItemByBarcode(
  token: string,
  barcodeQuery: string,
  chainId: number
): Promise<RecognitionReviewBarcodeLookupResponse> {
  const params = new URLSearchParams({ chain_id: String(chainId) });
  return forgeRequest<RecognitionReviewBarcodeLookupResponse>(
    token,
    `${FORGE_PR_REVIEW_API_URL}/catalog/barcode/${encodeURIComponent(barcodeQuery)}?${params}`,
    'Error fetching Recognition catalog evidence by barcode'
  );
}

export async function fetchItemByDesignId(
  token: string,
  designId: string,
  chainId: number
): Promise<ApiMasterLabelDesignLookupResponse> {
  const params = new URLSearchParams({
    chain_id: String(chainId),
  });
  return forgeRequest<ApiMasterLabelDesignLookupResponse>(
    token,
    `${FORGE_MASTER_LABEL_API_URL}/design/${encodeURIComponent(designId)}?${params}`,
    'Error fetching item data by design id'
  );
}

export async function saveState(
  token: string,
  request: MasterLabelStateSaveRequest
): Promise<MasterLabelStateSaveResponse> {
  return forgeRequest<MasterLabelStateSaveResponse>(
    token,
    `${FORGE_MASTER_LABEL_API_URL}/state`,
    'Error saving Master Label state',
    { method: 'PUT', body: request }
  );
}

export async function fetchItemsByCropIds(
  token: string,
  cropIds: string[],
  chainId: number
): Promise<ApiMasterLabelCropLookupResponse> {
  const params = new URLSearchParams({
    chain_id: String(chainId),
  });
  cropIds.forEach((cropId) => params.append('crop_id', cropId));
  return forgeRequest<ApiMasterLabelCropLookupResponse>(
    token,
    `${FORGE_MASTER_LABEL_API_URL}/crops?${params}`,
    'Error fetching item data by crop ids'
  );
}

export async function searchEmbeddingsByCropIds(
  token: string,
  embeddingCropIds: string[]
): Promise<CropEmbeddingSearchResponse[]> {
  const params = new URLSearchParams();
  embeddingCropIds.forEach((id) => {
    params.append('embedding_crop_ids', id);
  });
  params.append('include_item_number', 'false');

  return forgeRequest<CropEmbeddingSearchResponse[]>(
    token,
    `${CONTROL_EMBEDDING_API_URL}/by-ids?${params}`,
    'Error fetching embedding crops by ids'
  );
}

export async function resolveMasterLabelLinkTargets(
  token: string,
  request: ApiMasterLabelLinkTargetsRequest
): Promise<ApiMasterLabelLinkTargetsResponse> {
  return forgeRequest<ApiMasterLabelLinkTargetsResponse>(
    token,
    `${FORGE_MASTER_LABEL_API_URL}/link-targets`,
    'Error resolving Master Label link targets',
    {
      method: 'POST',
      body: {
        chain_id: request.chainId,
        item_numbers: request.itemNumbers ?? [],
        normalized_barcodes: request.normalizedBarcodes ?? [],
        design_ids: request.designIds ?? [],
        crop_ids: request.cropIds ?? [],
      },
    }
  );
}

export async function searchDesignGroups(
  token: string,
  chainId: number,
  query: string,
  page = 1,
  limit = 20
): Promise<ForgeDesignGroupSearchResponse> {
  const params = new URLSearchParams({
    chain_id: String(chainId),
    query,
    page: String(page),
    limit: String(limit),
  });
  return forgeRequest<ForgeDesignGroupSearchResponse>(
    token,
    `${FORGE_DESIGN_GROUPS_API_URL}/search?${params}`,
    'Error searching Forge design groups'
  );
}

export async function modifyDesignGroups(
  token: string,
  request: ModifyForgeDesignGroupsRequest
): Promise<ModifyForgeDesignGroupsResponse> {
  return forgeRequest<ModifyForgeDesignGroupsResponse>(
    token,
    `${FORGE_DESIGN_GROUPS_API_URL}`,
    'Error updating Forge design groups',
    { method: 'POST', body: request }
  );
}

/**
 * Uses API-DL's exact admin-group decision to gate the two Recognition admin surfaces without
 * adding a new permission or changing API Main.
 */
export async function fetchRecognitionReviewAccess(
  token: string
): Promise<RecognitionReviewAccessResponse> {
  return forgeRequest<RecognitionReviewAccessResponse>(
    token,
    `${FORGE_PR_REVIEW_API_URL}/access`,
    'Error checking PR review access'
  );
}

export async function fetchRecognitionThresholdConfiguration(
  token: string
): Promise<RecognitionThresholdConfiguration> {
  return forgeRequest<RecognitionThresholdConfiguration>(
    token,
    `${FORGE_PR_REVIEW_API_URL}/confidence-thresholds`,
    'Error loading Recognition thresholds'
  );
}

export async function saveRecognitionThresholdDraft(
  token: string,
  request: RecognitionThresholdDraftRequest
): Promise<RecognitionThresholdConfiguration> {
  return forgeRequest<RecognitionThresholdConfiguration>(
    token,
    `${FORGE_PR_REVIEW_API_URL}/confidence-thresholds/draft`,
    'Error saving Recognition threshold draft',
    { method: 'PUT', body: request }
  );
}

export async function activateRecognitionThresholdRevision(
  token: string,
  revisionId: string
): Promise<void> {
  return forgeVoidRequest(
    token,
    `${FORGE_PR_REVIEW_API_URL}/confidence-thresholds/${encodeURIComponent(revisionId)}/activate`,
    'Error activating Recognition thresholds',
    { method: 'POST' }
  );
}

export async function clearConfidentCorrectErrorBuckets(
  token: string
): Promise<RecognitionConfidentCorrectBucketCleanupResponse> {
  return forgeRequest<RecognitionConfidentCorrectBucketCleanupResponse>(
    token,
    `${FORGE_PR_REVIEW_API_URL}/confident-correct/error-buckets`,
    'Error removing confident-correct error buckets',
    { method: 'DELETE' }
  );
}

function appendRecognitionPopulationQuery(
  query: URLSearchParams,
  params: Pick<RecognitionOkrAggregatesParams, 'modPrefix' | 'collectorValues'>
): void {
  if (params.modPrefix) query.set('mod_prefix', params.modPrefix);
  for (const email of params.collectorValues ?? []) {
    query.append('collector_values', email);
  }
}

/**
 * Lists capture units awaiting PR review for one chain week, in the requested stable
 * server-side order. Omitting `weekStart` lets the backend resolve the default week and echo it
 * on the response.
 *
 * `modTag`, `modPrefix`, `collectorValues` (repeatable exact emails), `capturedDate`, `strategy`,
 * and `cell` narrow the queue server-side; the response echoes the filters it actually applied.
 * The OKRs dashboard reads its unit table here with `strategy=representative`.
 */
export async function fetchRecognitionReviewQueue(
  token: string,
  params: RecognitionReviewQueueParams
): Promise<RecognitionReviewQueueResponse> {
  const query = new URLSearchParams();
  if (params.chainId !== null && params.chainId !== undefined) {
    query.set('chain_id', String(params.chainId));
  }
  if (params.period) query.set('period', params.period);
  if (params.capturedFrom) query.set('captured_from', params.capturedFrom);
  if (params.capturedTo) query.set('captured_to', params.capturedTo);
  if (params.weekStart) query.set('week_start', params.weekStart);
  if (params.page) query.set('page', String(params.page));
  if (params.limit) query.set('limit', String(params.limit));
  if (params.modTag) query.set('mod_tag', params.modTag);
  appendRecognitionPopulationQuery(query, params);
  if (params.capturedDate) query.set('captured_date', params.capturedDate);
  if (params.capturedDateTo) query.set('captured_date_to', params.capturedDateTo);
  if (params.strategy) query.set('strategy', params.strategy);
  if (params.cell) query.set('cell', params.cell);
  if (params.bucket) query.set('bucket', params.bucket);
  if (params.reviewedOnly) query.set('reviewed_only', 'true');
  if (params.sort) query.set('sort', params.sort);

  const requestKey = `${token}:${query.toString()}`;
  return forgeRequest<RecognitionReviewQueueResponse>(
    token,
    `${FORGE_PR_REVIEW_API_URL}/queue?${query.toString()}`,
    'Error fetching PR review queue',
    { dedupeKey: ['fetchRecognitionReviewQueue', requestKey] }
  );
}

/**
 * Fetches the weekly Recognition OKR aggregates: both outcome grids, adjustment reasons, scan
 * issues, and the bucket Pareto. Metrics only - unit rows come from the paginated queue endpoint.
 */
export async function fetchRecognitionOkrAggregates(
  token: string,
  params: RecognitionOkrAggregatesParams
): Promise<RecognitionOkrAggregatesResponse> {
  const query = new URLSearchParams();
  if (params.chainId !== null && params.chainId !== undefined) {
    query.set('chain_id', String(params.chainId));
  }
  if (params.period) query.set('period', params.period);
  if (params.capturedFrom) query.set('captured_from', params.capturedFrom);
  if (params.capturedTo) query.set('captured_to', params.capturedTo);
  if (params.weekStart) query.set('week_start', params.weekStart);
  appendRecognitionPopulationQuery(query, params);
  params.reviewClass?.forEach((value) => query.append('review_class', value));
  params.eligibility?.forEach((value) => query.append('eligibility', value));
  params.identity?.forEach((value) => query.append('identity', value));
  params.scanState?.forEach((value) => query.append('scan_state', value));
  params.cell?.forEach((value) => query.append('cell', value));
  if (params.bucket) query.set('bucket', params.bucket);
  if (params.topKRanking) query.set('top_k_ranking', params.topKRanking);
  if (params.topK) query.set('top_k', String(params.topK));

  const requestKey = `${token}:${query.toString()}`;
  return forgeRequest<RecognitionOkrAggregatesResponse>(
    token,
    `${FORGE_PR_REVIEW_API_URL}/aggregates?${query.toString()}`,
    'Error fetching Recognition OKR aggregates',
    { dedupeKey: ['fetchRecognitionOkrAggregates', requestKey] }
  );
}

/** Lists individual reviewed samples matching the complete dashboard filter intersection. */
export async function fetchRecognitionReviewSlice(
  token: string,
  params: RecognitionReviewSliceParams
): Promise<RecognitionReviewSliceResponse> {
  const query = new URLSearchParams();
  if (params.chainId !== null && params.chainId !== undefined) {
    query.set('chain_id', String(params.chainId));
  }
  if (params.period) query.set('period', params.period);
  if (params.capturedFrom) query.set('captured_from', params.capturedFrom);
  if (params.capturedTo) query.set('captured_to', params.capturedTo);
  appendRecognitionPopulationQuery(query, params);
  params.reviewClass?.forEach((value) => query.append('review_class', value));
  params.eligibility?.forEach((value) => query.append('eligibility', value));
  params.identity?.forEach((value) => query.append('identity', value));
  params.scanState?.forEach((value) => query.append('scan_state', value));
  params.cell?.forEach((value) => query.append('cell', value));
  if (params.bucket) query.set('bucket', params.bucket);
  if (params.topKRanking) query.set('top_k_ranking', params.topKRanking);
  if (params.topK) query.set('top_k', String(params.topK));
  if (params.cursor) query.set('cursor', params.cursor);
  if (params.limit) query.set('limit', String(params.limit));
  return forgeRequest<RecognitionReviewSliceResponse>(
    token,
    `${FORGE_PR_REVIEW_API_URL}/slice?${query.toString()}`,
    'Error fetching Recognition review slice',
    { signal: params.signal }
  );
}

/**
 * Fetches one capture unit's samples with their frozen recognition evidence. An omitted `modTag`
 * matches the samples captured without a mod tag, which is a distinct unit from any tagged one.
 */
export async function fetchRecognitionReviewUnit(
  token: string,
  params: RecognitionReviewUnitParams
): Promise<RecognitionReviewUnitDetailResponse> {
  const query = new URLSearchParams({ chain_id: String(params.chainId) });
  if (params.modTag) query.set('mod_tag', params.modTag);

  const requestKey = `${token}:${params.imageId}:${query.toString()}`;
  return forgeRequest<RecognitionReviewUnitDetailResponse>(
    token,
    `${FORGE_PR_REVIEW_API_URL}/units/${encodeURIComponent(String(params.imageId))}?${query.toString()}`,
    'Error fetching PR review unit',
    { dedupeKey: ['fetchRecognitionReviewUnit', requestKey] }
  );
}

/**
 * Saves one reviewer verdict and returns the outcome the backend scored and stored for it, plus
 * the sample's post-write `updatedAt` for the next verdict's optimistic-concurrency token.
 */
export async function saveRecognitionReview(
  token: string,
  evaluationSampleId: string,
  request: RecognitionReviewRequest
): Promise<RecognitionReviewSaveResponse> {
  return forgeRequest<RecognitionReviewSaveResponse>(
    token,
    `${FORGE_PR_REVIEW_API_URL}/samples/${encodeURIComponent(evaluationSampleId)}/review`,
    'Error saving PR review',
    { method: 'PUT', body: request }
  );
}

/** Confirm every currently eligible confident-correct scan with one unit-scoped request. */
export async function bulkConfirmConfidentCorrect(
  token: string,
  chainId: number,
  request: RecognitionBulkConfirmRequest
): Promise<RecognitionBulkConfirmResponse> {
  return forgeRequest<RecognitionBulkConfirmResponse>(
    token,
    `${FORGE_PR_REVIEW_API_URL}/units/bulk-confirm-confident-correct?chain_id=${encodeURIComponent(String(chainId))}`,
    'Error confirming confident correct scans',
    { method: 'PUT', body: request }
  );
}

/**
 * Stamps every sample of one capture unit with the strategy its crops were collected under.
 * The write does not change any review concurrency token.
 */
export async function setRecognitionSamplingStrategy(
  token: string,
  chainId: number,
  request: RecognitionSamplingStrategyRequest
): Promise<RecognitionSamplingStrategyResponse> {
  const query = new URLSearchParams({ chain_id: String(chainId) });
  return forgeRequest<RecognitionSamplingStrategyResponse>(
    token,
    `${FORGE_PR_REVIEW_API_URL}/units/sampling-strategy?${query.toString()}`,
    'Error updating Recognition sampling strategy',
    { method: 'PUT', body: request }
  );
}

/**
 * Fetches an image (e.g. a crop or product photo) as a Blob using session
 * credentials. Shared by flows that re-upload or convert existing images.
 */
export async function fetchImageBlob(url: string): Promise<Blob> {
  // CDN fetch: cookie credentials, no auth header, and no 401-logout side
  // effect — only the thrown error type is unified with the transport.
  const response = await fetch(url, { credentials: 'include' });
  if (!response.ok) {
    throw new ForgeApiError(
      `Failed to fetch image: ${response.status} ${response.statusText} (${url})`,
      response.status
    );
  }
  return response.blob();
}
